/**
 * The public website's shared vocabulary: one nav, one set of control styles,
 * the Chronicle formatters, per-route <head> tags and the legacy-URL map.
 *
 * The site is ONE website: the landing (`/`), the Codex (`/guide`), the
 * Chronicle (`/news`, `/news/:slug`) and the legal pages (`/legal/:slug`) all
 * carry the same SiteHeader / SiteFooter. What they share lives here so the
 * Vue components and the string-rendered landing cannot drift apart.
 */
import { t, activeLocale, localeTag } from '../i18n';

/** The brand lockup (the veiled moon disc + wordmark, horizontal, transparent),
 *  served from public/ — a bound src keeps the SFC compiler from inlining it,
 *  and a plain URL keeps the ~800-file art index out of the routing bundle.
 *  Composed from generated/brand/moon-icon-a.webp and the wordmark cut out of
 *  generated/brand/logo-lockup.webp by scripts/make-brand-assets.py; the same
 *  file as art.WORDMARK. */
export const WORDMARK = '/landing/logo.webp';

export type SiteSection = 'home' | 'guide' | 'news' | 'none';

export const FOCUS = 'focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-gold-bright';
export const WRAP = 'mx-auto w-full max-w-6xl px-4 sm:px-8';
export const BTN_PRIMARY =
  'inline-flex min-h-11 items-center justify-center rounded-sm border border-gold bg-crimson px-6 font-label '
  + 'text-base font-semibold uppercase tracking-widest text-text shadow-lift transition-transform '
  + `active:translate-y-px motion-reduce:transition-none ${FOCUS}`;
export const BTN_QUIET =
  'inline-flex min-h-11 items-center justify-center rounded-sm border border-border bg-surface-2/70 px-6 '
  + `font-label text-base font-semibold uppercase tracking-widest text-text-dim active:text-text ${FOCUS}`;
export const NAV_LINK =
  'inline-flex min-h-11 items-center px-2 font-label text-sm font-semibold uppercase tracking-wider '
  + `text-text-dim aria-[current=page]:text-gold-bright aria-[current=location]:text-gold-bright active:text-text ${FOCUS}`;
export const DRAWER_LINK =
  'flex min-h-11 items-center border-b border-border/60 px-1 font-label text-base font-semibold uppercase '
  + `tracking-wider text-text aria-[current=page]:text-gold-bright active:text-gold-bright ${FOCUS}`;
/** One row in a footer link column: flush left, full width, a 44px target. */
export const FOOT_LINK =
  'inline-flex min-h-11 w-full items-center text-sm text-text-dim underline-offset-4 '
  + `hover:text-text active:text-text ${FOCUS}`;
/** A footer column heading: words, 18px, so the display face carries it. */
export const FOOT_HEAD =
  'font-display text-lg uppercase tracking-[0.12em] text-gold-bright';

/**
 * THE WHOLE-CARD HIT AREA (owner, 21.09) — the stretched-link pattern.
 *
 * `CARD_HIT` goes on the card element: it makes the card the positioning
 * context for the overlay, lifts and warms its border on hover, and — because
 * the only focusable thing inside is the title link — draws the focus ring
 * around the WHOLE CARD via `:has(:focus-visible)`. The lift is a transform,
 * so it is GPU-only, and it is fenced behind `motion-safe`.
 *
 * `CARD_STRETCH` goes on the card's ONE real link (the title `<a>`): an empty
 * `::after` stretched over the card turns every pixel of it into that link's
 * hit area. The link keeps its own ring suppressed because the card draws it.
 *
 * One link per card is the point: the "Read ->" affordance stays a plain
 * `<span>` (static, so the overlay sits above it and a tap on it navigates),
 * and the accessibility tree sees a single link with the entry's title as its
 * name instead of two links to the same place.
 */
export const CARD_HIT =
  'relative motion-safe:transition-transform motion-safe:duration-200 hover:-translate-y-0.5 '
  + 'hover:border-gold/70 has-[a:focus-visible]:outline-2 has-[a:focus-visible]:outline-offset-2 '
  + 'has-[a:focus-visible]:outline-gold-bright';
export const CARD_STRETCH =
  'after:absolute after:inset-0 after:content-[""] focus-visible:outline-none active:text-gold-bright';

/**
 * THE ASH GROUND — the one surface the whole website stands on.
 *
 * Three fixed, cheap layers, exactly as the landing paints them (owner, 21.09):
 * the tiled plaster, a warm bronze bloom at the top, and a vignette that keeps
 * the long middle of a page from going flat. No parallax, no scroll listener,
 * no animated filter — nothing here moves, so `motion-reduce` has nothing to
 * turn off. They are `fixed` and negative-z, so they must be direct children
 * of the page scroller and the scroller must carry `isolate` (see
 * `SITE_ROOT_CLASS`), or they escape to the document root and vanish.
 *
 * The plaster URL is written as a Tailwind arbitrary value and must stay a
 * literal here — the v4 scanner reads source text, not runtime strings.
 */
export const GROUND_LAYERS = [
  "pointer-events-none fixed inset-0 -z-40 bg-[url('/landing/bg/plaster.webp')] bg-[length:720px_auto] bg-repeat opacity-15",
  'pointer-events-none fixed inset-0 -z-30 bg-[radial-gradient(120%_80%_at_50%_0%,rgba(176,138,74,0.12),transparent_60%)]',
  'pointer-events-none fixed inset-0 -z-30 bg-[radial-gradient(120%_100%_at_50%_50%,transparent_35%,rgba(20,18,15,0.75)_100%)]',
] as const;

/** The same three layers as markup, for the string-rendered pages (landing, legal). */
export const groundHtml = (): string =>
  GROUND_LAYERS.map((c) => `<div aria-hidden="true" class="${c}"></div>`).join('');

/** The hero landscape plate a page puts behind its masthead, under a wash that
 *  keeps the heading contrast well clear of AA. Public URL, never bundled. */
export const HERO_PLATE = '/landing/bg/landscape.webp';

/** The scroller every site page renders into (the SPA body never scrolls).
 *  `isolate`: the ground layers above are negative-z and need a stacking
 *  context of their own. */
export const SITE_ROOT_CLASS =
  'isolate '
  +
  'fixed inset-0 overflow-x-hidden overflow-y-auto scroll-smooth bg-bg font-body text-lg leading-relaxed text-text '
  + 'antialiased motion-reduce:scroll-auto selection:bg-gold-bright selection:text-bg '
  + 'pl-[env(safe-area-inset-left)] pr-[env(safe-area-inset-right)]';

export interface NavItem { key: string; section: SiteSection | null; href: string; landingHref: string }

/** i18n keys landing.nav.<key>. `landingHref` is used on the landing itself (in-page anchors). */
export const SITE_NAV: NavItem[] = [
  { key: 'home', section: 'home', href: '/', landingHref: '#top' },
  { key: 'callings', section: null, href: '/#callings', landingHref: '#callings' },
  { key: 'city', section: null, href: '/#city', landingHref: '#city' },
  { key: 'rise', section: null, href: '/#rise', landingHref: '#rise' },
  { key: 'faq', section: null, href: '/#guides', landingHref: '#guides' },
  { key: 'guide', section: 'guide', href: '/guide', landingHref: '/guide' },
  { key: 'news', section: 'news', href: '/news', landingHref: '/news' },
];

/** The three founding papers, keyed to the footer's own i18n keys. */
export const LEGAL_LINKS = [
  { slug: 'terms-of-service', key: 'landing.footer.terms' },
  { slug: 'privacy-policy', key: 'landing.footer.privacy' },
  { slug: 'cookie-policy', key: 'landing.footer.cookies' },
];

// ── Chronicle formatting ───────────────────────────────────────────────────

const KNOWN_TAGS = ['updates', 'events', 'patch', 'family'];
export const CHRONICLE_TAGS = ['all', ...KNOWN_TAGS] as const;

/** A tag's label in the active tongue; unknown CMS tags render as written. */
export function tagLabel(tag: string | null | undefined): string {
  if (!tag) return '';
  return KNOWN_TAGS.includes(tag) ? t(`chronicle.tag.${tag}`) : tag;
}

/** Roman numerals for entry and page numbers (1..3999; anything else as digits). */
export function roman(n: number): string {
  if (!(n > 0) || n > 3999 || !Number.isInteger(n)) return String(n);
  const map: Array<[number, string]> = [
    [1000, 'M'], [900, 'CM'], [500, 'D'], [400, 'CD'], [100, 'C'], [90, 'XC'],
    [50, 'L'], [40, 'XL'], [10, 'X'], [9, 'IX'], [5, 'V'], [4, 'IV'], [1, 'I'],
  ];
  let out = '';
  for (const [v, s] of map) while (n >= v) { out += s; n -= v; }
  return out;
}

/** The real publish stamp (createdAt for legacy rows). */
export const entryIso = (a: { publishedAt?: string | null; createdAt?: string }) => a.publishedAt || a.createdAt || '';

/** "15 July 2026" in the active tongue, UTC so the day never shifts. */
export function fmtDate(a: { publishedAt?: string | null; createdAt?: string }): string {
  const iso = entryIso(a);
  if (!iso) return '';
  return new Date(iso).toLocaleDateString(localeTag(activeLocale()), {
    day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC',
  });
}

/** Hash a string to a stable unsigned int (the pale-sun emblem's placement). */
export function seedHash(seed: string): number {
  let h = 0;
  for (let i = 0; i < seed.length; i++) h = (h * 31 + seed.charCodeAt(i)) >>> 0;
  return h;
}

// ── <head> per route ───────────────────────────────────────────────────────

const META_SELECTORS: Array<[string, 'title' | 'description']> = [
  ['meta[name="description"]', 'description'],
  ['meta[property="og:title"]', 'title'],
  ['meta[property="og:description"]', 'description'],
  ['meta[name="twitter:title"]', 'title'],
  ['meta[name="twitter:description"]', 'description'],
];

/**
 * Set the document title and description (plus og/twitter copies) for a site
 * page. Returns a restore fn that puts back what the shell shipped with, so the
 * game and the landing keep the server-injected head.
 *
 * Crawlers get the shell's head: meta-inject.js renders one head for every
 * path, so per-route tags are client-side only.
 */
export function setPageMeta(meta: { title: string; description: string }): () => void {
  const prevTitle = document.title;
  const touched: Array<[HTMLMetaElement, string | null]> = [];
  document.title = meta.title;
  for (const [sel, field] of META_SELECTORS) {
    let el = document.head.querySelector<HTMLMetaElement>(sel);
    if (!el && sel === META_SELECTORS[0][0]) {
      el = document.createElement('meta');
      el.name = 'description';
      document.head.appendChild(el);
      touched.push([el, null]);
      el.content = meta[field];
      continue;
    }
    if (!el) continue;
    touched.push([el, el.getAttribute('content')]);
    el.setAttribute('content', meta[field]);
  }
  return () => {
    document.title = prevTitle;
    for (const [el, prev] of touched) {
      if (prev === null) el.remove();
      else el.setAttribute('content', prev);
    }
  };
}

// ── routes ─────────────────────────────────────────────────────────────────

/**
 * Old static-page URLs → their SPA routes (the Codex, the Chronicle list and
 * the article reader used to be separate .html documents). Returns null for
 * anything that is not a legacy URL. An article URL without a slug lands on
 * the Chronicle.
 */
const LEGACY_PAGE = /^\/(guide|news)\.html$/;
const LEGACY_ARTICLE = /^\/article(\.html)?$/;

export function legacyRedirect(pathname: string, search: string): string | null {
  const path = pathname.replace(/\/+$/, '') || '/';
  const page = LEGACY_PAGE.exec(path);
  if (page) return `/${page[1]}`;
  if (LEGACY_ARTICLE.test(path)) {
    const slug = new URLSearchParams(search).get('slug');
    return slug ? `/news/${encodeURIComponent(slug)}` : '/news';
  }
  return null;
}

export type SiteRoute =
  | { page: 'guide' }
  | { page: 'news' }
  | { page: 'article'; slug: string }
  | { page: 'legal'; slug: string };

/** Which site page a path is, if any. Trailing slashes are tolerated. */
export function matchSiteRoute(pathname: string): SiteRoute | null {
  const path = pathname.replace(/\/+$/, '') || '/';
  if (path === '/guide') return { page: 'guide' };
  if (path === '/news') return { page: 'news' };
  const m = /^\/news\/([^/]+)$/.exec(path);
  if (m) {
    let slug = m[1];
    try { slug = decodeURIComponent(slug); } catch { /* keep the raw segment */ }
    return { page: 'article', slug };
  }
  const legal = /^\/legal\/([^/]+)$/.exec(path);
  if (legal) {
    let slug = legal[1];
    try { slug = decodeURIComponent(slug); } catch { /* keep the raw segment */ }
    return { page: 'legal', slug };
  }
  return null;
}
